import React from "react";
import { Marker, InfoWindow, Polyline } from "@react-google-maps/api";
import { Tag } from "primereact/tag";

export type Order = {
  orderID: number;
  gpsLocation: string;
  sequenceNo?: number;
  driverID?: number | null;
  driverName?: string | null;
  driverNumber?: string | null;
  taxableAmount?: number | null;
  userName?: string | null;
  userNumber?: string | null;
  isInvoiced?: boolean | null;
  delayedStatusId?: number | null;
  delayedStatusName?: string | null;
  totalAssignedTodayForDriver?: number | null;
  totalInvoicedForDriver?: number | null;
  totalCancelledForDriver?: number | null;
  totalReturnOrCancelled?: number | null;
  isCancelled?: number | boolean | null;
  invoicedDate?: string | null;
  modifiedDateStatus?: string | null; // new field
  color?: string | null;
};

type Props = {
  markers: Order[];
  groups: Record<string, Order[]>;
  selectedLocKey: string | null;
  setSelectedLocKey: (k: string | null) => void;
  selectedOrderId: number | null;
  setSelectedOrderId: (id: number | null) => void;
  mapRef: React.MutableRefObject<google.maps.Map | null>;
  mapCenter: { lat: number; lng: number };
  setMapCenter: (pos: { lat: number; lng: number }) => void;
   highlightLocKeys: string[]
  selectedDriverId: number | null;
};

function parseGpsLocation(gps?: string | null): { lat: number; lng: number } | null {
  if (!gps || !gps.includes(",")) return null;
  const [a, b] = gps.split(",");
  const lat = parseFloat(a.trim());
  const lng = parseFloat(b.trim());
  return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}
function gpsKey(gps?: string | null): string | null {
  if (!gps) return null;
  return gps.replace(/\s+/g, "").trim();
}

function pinSvgUrl(color: string, scale = 1) {
  const size = Math.round(36 * scale);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24"><path d="M12 2c-4.418 0-8 3.582-8 8 0 5.25 8 12 8 12s8-6.75 8-12c0-4.418-3.582-8-8-8z" fill="${color}" stroke="white" stroke-width="1.5"/></svg>`;
  return "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg);
}
function getIcon(color: string = "#1976d2", scale = 1): google.maps.Icon {
  const size = Math.round(36 * scale);
  return {
    url: pinSvgUrl(color, scale),
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(Math.round(size / 2), size),
  };
}

function getDisplaySeq(order: Order): number | null {
  return (order as any).sequenceNo ?? null;
}

const COLORS = {
  deliveredGreen: "#22c55e",
  cancelledRed: "#ef4444",
  // openOrange: "#ddd72bff",
  openOrange: "#3b82f6",
 
};

export default function OrdersLayer({
  markers,
  groups,
  selectedLocKey,
  setSelectedLocKey,
  selectedOrderId,
  setSelectedOrderId,
  mapRef,
  mapCenter,
  setMapCenter,
  highlightLocKeys,
  selectedDriverId,
}: Props) {
  const [phoneSearchOrders, setPhoneSearchOrders] = React.useState<Order[]>([]);

  // function handlePhoneSearch(phone: string) {
  // const foundOrders = markers.filter(
  //   (o) => o.userNumber === phone 
  // );
  function handlePhoneSearch(phone: string) {
  const foundOrders = markers.filter(o => o.userNumber === phone);
  setPhoneSearchOrders(foundOrders);

  if (foundOrders.length > 0) {
    // Optional: center map on first order
    const firstPos = parseGpsLocation(foundOrders[0].gpsLocation);
    if (firstPos) {
      setMapCenter(firstPos);
      mapRef.current?.panTo(firstPos);
    }
  }
}
  const allOrders = React.useMemo(() => Object.values(groups).flat(), [groups]);

  const driverStops = React.useMemo(() => {
    if (!selectedDriverId) return [];
    const list = allOrders
      .filter((o) => o.driverID === selectedDriverId)
      .map((o) => ({ o, p: parseGpsLocation(o.gpsLocation) }))
      .filter((x) => x.p !== null)
      .sort((a, b) => ((a.o.sequenceNo ?? 0) - (b.o.sequenceNo ?? 0)))
      .map((x) => x.p!) as { lat: number; lng: number }[];

    const dedup: { lat: number; lng: number }[] = [];
    for (const p of list) {
      const last = dedup[dedup.length - 1];
      if (!last || last.lat !== p.lat || last.lng !== p.lng) dedup.push(p);
    }
    return dedup;
  }, [selectedDriverId, allOrders]);

  const [stitchedPath, setStitchedPath] = React.useState<google.maps.LatLngLiteral[] | null>(null);

  function routePromise(req: google.maps.DirectionsRequest) {
    return new Promise<google.maps.DirectionsResult>((resolve, reject) => {
      const ds = new google.maps.DirectionsService();
      ds.route(req, (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) resolve(result);
        else reject({ status, result });
      });
    });
  }
  const makeRequestFor = (arr: { lat: number; lng: number }[], start: number, end: number): google.maps.DirectionsRequest => {
    const origin = new google.maps.LatLng(arr[start].lat, arr[start].lng);
    const destination = new google.maps.LatLng(arr[end].lat, arr[end].lng);
    const intermediates = arr.slice(start + 1, end).map((p) => ({ location: new google.maps.LatLng(p.lat, p.lng), stopover: false }));
    return { origin, destination, travelMode: google.maps.TravelMode.DRIVING, waypoints: intermediates, optimizeWaypoints: false };
  };

  React.useEffect(() => {
  if (!selectedDriverId || driverStops.length === 0) return;

  // Center on the first stop of the driver
  const firstStop = driverStops[0];
  setMapCenter(firstStop);
  mapRef.current?.panTo(firstStop);
  mapRef.current?.setZoom(12); // adjust zoom level as desired
}, [selectedDriverId, driverStops, mapRef, setMapCenter]);


  async function tryRouteChunk(arr: { lat: number; lng: number }[], s: number, e: number, minChunkSize = 2): Promise<google.maps.LatLngLiteral[]> {
    if (e <= s) return [];
    const count = e - s + 1;
    if (count <= 2) return arr.slice(s, e + 1).map((p) => ({ lat: p.lat, lng: p.lng }));
    try {
      const req = makeRequestFor(arr, s, e);
      const result = await routePromise(req);
      const overview = result.routes?.[0]?.overview_path ?? [];
      const coords = overview.map((pt: google.maps.LatLng) => ({ lat: pt.lat(), lng: pt.lng() }));
      if (coords.length > 0) return coords;
      throw new Error("Empty overview_path");
    } catch (err) {
      const mid = Math.floor((s + e) / 2);
      if (mid === s || mid === e) return arr.slice(s, e + 1).map((p) => ({ lat: p.lat, lng: p.lng }));
      const left = await tryRouteChunk(arr, s, mid, minChunkSize);
      const right = await tryRouteChunk(arr, mid, e, minChunkSize);
      const stitched = [...left];
      const lastLeft = left[left.length - 1];
      const firstRight = right[0];
      if (Math.abs(lastLeft.lat - firstRight.lat) < 1e-7 && Math.abs(lastLeft.lng - firstRight.lng) < 1e-7) stitched.push(...right.slice(1));
      else stitched.push(...right);
      return stitched;
    }
  }

  React.useEffect(() => {
    let cancelled = false;
    setStitchedPath(null);

    async function compute() {
      if (!selectedDriverId || driverStops.length < 2) return;
      const arr = driverStops;
      const MAX_POINTS_PER_REQUEST = 25;
      const n = arr.length;
      const segments: [number, number][] = [];
      let start = 0;
      while (start < n - 1) {
        const end = Math.min(start + MAX_POINTS_PER_REQUEST - 1, n - 1);
        segments.push([start, end]);
        start = end;
      }

      const stitchedOverall: google.maps.LatLngLiteral[] = [];

      for (let i = 0; i < segments.length; i++) {
        if (cancelled) return;
        const [s, e] = segments[i];
        try {
          const coordsForChunk = await tryRouteChunk(arr, s, e, 2);
          if (!coordsForChunk || coordsForChunk.length === 0) continue;
          if (stitchedOverall.length === 0) stitchedOverall.push(...coordsForChunk);
          else {
            const last = stitchedOverall[stitchedOverall.length - 1];
            const firstOfChunk = coordsForChunk[0];
            if (Math.abs(last.lat - firstOfChunk.lat) < 1e-7 && Math.abs(last.lng - firstOfChunk.lng) < 1e-7) stitchedOverall.push(...coordsForChunk.slice(1));
            else stitchedOverall.push(...coordsForChunk);
          }
        } catch {
          const fallback = arr.slice(s, e + 1).map((p) => ({ lat: p.lat, lng: p.lng }));
          if (stitchedOverall.length === 0) stitchedOverall.push(...fallback);
          else {
            const last = stitchedOverall[stitchedOverall.length - 1];
            const firstOfFallback = fallback[0];
            if (Math.abs(last.lat - firstOfFallback.lat) < 1e-7 && Math.abs(last.lng - firstOfFallback.lng) < 1e-7) stitchedOverall.push(...fallback.slice(1));
            else stitchedOverall.push(...fallback);
          }
        }
      }
      if (!cancelled) setStitchedPath(stitchedOverall);
    }

    compute().catch(console.error);
    return () => { cancelled = true; };
  }, [selectedDriverId, driverStops]);

  const keyToLatLng = (key: string | null) => {
    if (!key) return null;
    const any = groups[key]?.[0];
    return parseGpsLocation(any?.gpsLocation);
  };

  return (
    <>

{selectedDriverId && stitchedPath && stitchedPath.length > 1 && (() => {

  const driverOrder = markers.find((o) => o.driverID === selectedDriverId);
  const strokeColor = driverOrder?.color ?? "#1976d2"; 

  return (
    <Polyline
      path={stitchedPath}
      options={{
        strokeColor: strokeColor,
        strokeOpacity: 0.95,
        strokeWeight: 4,
        zIndex: 500,
      }}
    />
  );
})()}

      {markers
        .filter(m => !selectedDriverId || m.driverID === selectedDriverId)
        .map((m) => {
          const pos = parseGpsLocation(m.gpsLocation);
          const key = gpsKey(m.gpsLocation);
          if (!pos || !key) return null;
          const group = groups[key] ?? [];
          const anyInvoiced = group.some((x) => !!x.isInvoiced);
          const anyCancelled = group.some((x) => x.isCancelled === true || x.isCancelled === 1);
          const anyDelayed = group.some(x => !!x.delayedStatusId);
          const isSelectedLoc = selectedLocKey === key;
          // const isBouncing = highlightLocKeys.includes(key);
          const isPhoneSearchOrder = phoneSearchOrders.some(o => o.orderID === m.orderID);
          const isBouncing = highlightLocKeys.includes(key) || isPhoneSearchOrder;

          const fillColor =
     anyDelayed
    ? COLORS.cancelledRed 
    : anyInvoiced
    ? COLORS.deliveredGreen
    : anyCancelled
    ? COLORS.cancelledRed
    : COLORS.openOrange;
          const groupHasSelectedDriver = selectedDriverId != null && group.some((o) => o.driverID === selectedDriverId);
          const markerBelongsToSelectedDriver = selectedDriverId != null && (m.driverID === selectedDriverId || groupHasSelectedDriver);
          const isOtherDriver = selectedDriverId != null && !markerBelongsToSelectedDriver;
          const icon = getIcon(fillColor, isOtherDriver ? 0.75 : 1);
          const opacity = isOtherDriver ? 0.45 : 1;
          let label: google.maps.MarkerLabel | undefined;
          if (selectedDriverId && markerBelongsToSelectedDriver) {
            const seq = getDisplaySeq(m);
            if (typeof seq === "number" && Number.isFinite(seq)) label = { text: String(seq), color: "#fff", fontSize: "12px" };
          }

          return (
            <Marker
              key={key}
              position={pos}
              icon={icon}
              animation={isBouncing ? google.maps.Animation.BOUNCE : undefined}
              zIndex={isSelectedLoc || isBouncing ? 999999 : undefined}
              label={label}
              onClick={() => {
                setSelectedLocKey(key);
                const def = group[0] ?? null;
                setSelectedOrderId(def?.orderID ?? null);
                setMapCenter(pos);
                mapRef.current?.panTo(pos);
              }}
              clickable
            />
          );
        })
        .filter(Boolean)}

      {/* InfoWindow */}
      {selectedLocKey && (
        <InfoWindow
          position={keyToLatLng(selectedLocKey) ?? mapCenter}
          onCloseClick={() => {
            setSelectedLocKey(null);
            setSelectedOrderId(null);
          }}
          options={{ pixelOffset: new google.maps.Size(0, -40) }}
        >
          {(() => {
            const ordersHereRaw = groups[selectedLocKey] ?? [];
            const ordersHere = Array.from(new Map(ordersHereRaw.map((o) => [o.orderID, o])).values());
            const many = ordersHere.length > 1;
            const selectedOrder = ordersHere.find((x) => x.orderID === selectedOrderId) ?? ordersHere[0] ?? null;


            const borderColor = selectedOrder?.isInvoiced
  ? COLORS.deliveredGreen
  : selectedOrder?.isCancelled === 1 || selectedOrder?.delayedStatusId
  ? COLORS.cancelledRed
  : COLORS.openOrange;
            return (
              <div style={{ background: "#fff", padding: 12, borderRadius: 8, borderTop: `2px solid ${borderColor}`, minWidth: 280 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                  <h3 style={{ marginBottom: -10, fontSize: 16 }}>Order Details</h3>
                  
                  <button
                    onClick={() => {
                      setSelectedLocKey(null);
                      setSelectedOrderId(null);
                    }}
                    style={{ background: "transparent", border: "none", fontSize: 22, cursor: "pointer" }}
                  >
                    ×
                  </button>
                </div>
                <hr style={{ color: "black", margin: "4px 0" }} />

                <div style={{ fontWeight: 600, marginBottom: many ? 8 : 0,marginTop: 10 }}>Orders at this location ({ordersHere.length})</div>
                {many && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                    {ordersHere.map((o) => (
                      <button
                        key={o.orderID}
                        onClick={() => setSelectedOrderId(o.orderID)}
                        style={{
                          border: "1px solid #e5e7eb",
                          padding: "4px 8px",
                          borderRadius: 12,
                          background: selectedOrder?.orderID === o.orderID ? "#111827" : "#fff",
                          color: selectedOrder?.orderID === o.orderID ? "#fff" : "#111827",
                          cursor: "pointer",
                        }}
                        title={`Seq ${getDisplaySeq(o) ?? "-"}`}
                      >
                        #{o.orderID}
                      </button>
                    ))}
                  </div>
                )}

                {selectedOrder ? (
                  <div>
                    <p style={{ margin: "6px 0" }}>
                      <b>Order ID:</b> {selectedOrder.orderID} <span style={{ opacity: 0.7 }}>(Seq {getDisplaySeq(selectedOrder) ?? "-"})</span>
                    </p>
                    <p style={{ margin: "6px 0" }}>
                      <b>Delivery Executive:</b> {selectedOrder.driverName ?? "—"} - {selectedOrder.driverNumber ?? "—"}
                    </p>
                    <p style={{ margin: "6px 0" }}>
                      <b>Status:</b>{" "}
                      <span style={{ color: selectedOrder.isInvoiced ? COLORS.deliveredGreen : selectedOrder.isCancelled === 1  || selectedOrder.delayedStatusId ? COLORS.cancelledRed : COLORS.openOrange, fontWeight: 600 }}>
                        {selectedOrder.isCancelled === 1
                          ? "Order is cancelled"
                          : selectedOrder.delayedStatusId && !selectedOrder.isInvoiced
                          ? selectedOrder.delayedStatusName ?? "Delayed"
                          : !selectedOrder.isInvoiced
                          ? "Yet to be delivered"
                          : "Delivered"}
                      </span>
                    </p>

                    {selectedOrder.isInvoiced && (
                      <p style={{ margin: "6px 0" }}>
                        <b>Delivery Date:</b> {selectedOrder.invoicedDate ? new Date(selectedOrder.invoicedDate).toLocaleString("en-GB") : "N/A"}
                      </p>
                    )}
                    {
                      selectedOrder.modifiedDateStatus && selectedOrder.delayedStatusId &&(
                        <p style={{ margin: "6px 0" }}>
                          <b>Last Updated:</b> {new Date(selectedOrder.modifiedDateStatus).toLocaleString("en-GB")}
                        </p>
                      )
                    }
                    <p style={{ margin: "6px 0" }}>
                      <b>Order Value:</b> ₹{(selectedOrder.taxableAmount ?? 0).toFixed(2)}
                    </p>
                    <p style={{ margin: "6px 0" }}>
                      <b>Customer:</b> {selectedOrder.userName ?? "—"} - {selectedOrder.userNumber ?? "—"}
                    </p>
                  </div>
                ) : (
                  <div>No order data.</div>
                )}
              </div>
            );
          })()}
        </InfoWindow> 
      )}
{phoneSearchOrders.map(order => {
  const pos = parseGpsLocation(order.gpsLocation);
  if (!pos) return null;

  const borderColor = order.isInvoiced
    ? COLORS.deliveredGreen
    : order.isCancelled === 1 || order.delayedStatusId
    ? COLORS.cancelledRed
    : COLORS.openOrange;

  return (
    <InfoWindow
      key={order.orderID}
      position={pos}
      onCloseClick={() => {
        setPhoneSearchOrders(prev => prev.filter(o => o.orderID !== order.orderID));
      }}
      options={{ pixelOffset: new google.maps.Size(0, -40) }}
    >
      <div style={{ background: "#fff", padding: 12, borderRadius: 8, borderTop: `2px solid ${borderColor}`, minWidth: 280 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3 style={{ marginBottom: -10, fontSize: 16 }}>Order Details</h3>
          <button
            onClick={() => setPhoneSearchOrders(prev => prev.filter(o => o.orderID !== order.orderID))}
            style={{ background: "transparent", border: "none", fontSize: 22, cursor: "pointer" }}
          >
            ×
          </button>
        </div>
        <hr style={{ color: "black", margin: "4px 0" }} />
        <p><b>Order ID:</b> {order.orderID} (Seq {getDisplaySeq(order) ?? "-"})</p>
        <p><b>Delivery Executive:</b> {order.driverName ?? "—"} - {order.driverNumber ?? "—"}</p>
        <p>
          <b>Status:</b>{" "}
          <span style={{ color: borderColor, fontWeight: 600 }}>
            {order.isCancelled === 1
              ? "Cancelled"
              : order.delayedStatusId && !order.isInvoiced
              ? order.delayedStatusName ?? "Delayed"
              : !order.isInvoiced
              ? "Yet to be delivered"
              : "Delivered"}
          </span>
        </p>
        <p><b>Order Value:</b> ₹{(order.taxableAmount ?? 0).toFixed(2)}</p>
        <p><b>Customer:</b> {order.userName ?? "—"} - {order.userNumber ?? "—"}</p>
      </div>
    </InfoWindow>
  );
})}

    </>
  );
}


